import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';

import { VehicleLookupContext } from 'Context/vehicleLookup';

import { wordpressImgToImgixURL } from 'Utilities';
import { useUpdatePageTitle } from 'Utilities/hooks/useUpdatePageTitle';

import { Content } from 'LegacySBCRA/Content/Content';

import ResponsiveImage from '../../../../../components/misc/ResponsiveImage/ResponsiveImage';
import DialogLoader from '../../../../../components/transitions/DialogLoader/DialogLoader';
import { createFAQData } from '../../../common/FAQSection/FAQSection';
import { RoundedSection } from '../../../common/RoundedSection/RoundedSection';
import SimilarArticles from '../../../common/SimilarArticles/SimilarArticles';
import { generateSimilarArticlesData } from '../../../common/SimilarArticles/SimilarArticlesHelpers';
import ContentSection from '../../../fastlane/contentSection/ContentSection';
import { ExpandableSection } from '../../../fastlane/expandableSection/ExpandableSection';
import TrackerFAQ from '../../Components/TrackerFAQ/TrackerFAQ';
import TrackerHero from '../../Components/TrackerHero/TrackerHero';
import TrackerUSP from '../../Components/TrackerUSP/TrackerUSP';
import { CUSTOMER_HUB_MILEAGE_ROUTE, CUSTOMER_HUB_VALUATION_ROUTE } from '../../CustomerHubHelpers';
import useFetchWordpressData from '../../Hooks/useFetchWordpressData';

import {
  generateContentSectionData,
  generateExpandableSectionData,
  getContentSectionTheme,
} from './TrackerHome.helpers';

import styles from './TrackerHome.scss';

// we could also use CUSTOMER_HUB_MILEAGE_ROUTE directly as useFetchWordpressData arg
const getWordpressDataUrl = (pathname:string) => ({
  [CUSTOMER_HUB_MILEAGE_ROUTE]: CUSTOMER_HUB_VALUATION_ROUTE,
  [CUSTOMER_HUB_VALUATION_ROUTE]: CUSTOMER_HUB_VALUATION_ROUTE,
}[pathname]);

const generateImage = ({
  imageAlt,
  imageArea,
  imgProps = {},
}: {
  imageAlt?: string;
  imageArea: string;
  imgProps?: object;
}) => (
  <ResponsiveImage
    imgProps={{
      alt: imageAlt,
      height: 440,
      loading: 'lazy',
      width: 440,
      ...imgProps,
    }}
    imgSrc={wordpressImgToImgixURL(imageArea)}
  />
);

const TrackerHome = () => {
  const history = useHistory();
  const { pathname } = useLocation();
  const { vehicleLookupState } = useContext(VehicleLookupContext);
  const { acf, apiError } = useFetchWordpressData(getWordpressDataUrl(pathname));
  const [loading, setLoading] = useState(!acf);

  useUpdatePageTitle({ acf, identifier: 'carValueTracker' });

  useEffect(() => {
    if (acf || apiError) {
      setLoading(false);
    }
  }, [acf, apiError]);

  useEffect(() => {
    if (!vehicleLookupState && history.location.pathname === CUSTOMER_HUB_MILEAGE_ROUTE) {
      history.replace(CUSTOMER_HUB_VALUATION_ROUTE);
    }
  }, [history, history.location, vehicleLookupState]);

  // Sep up Wordpress content
  const faq = useMemo(() => createFAQData(acf), [acf]);
  const contentSectionData = generateContentSectionData(acf);
  const expandableSectionData = generateExpandableSectionData(acf);
  const similarArticlesData = generateSimilarArticlesData(acf);

  if (loading) {
    return (<DialogLoader />);
  }

  return (
    <div className={styles.component}>
      <TrackerHero />
      <RoundedSection withBottomLedge className={styles.uspOuter} theme="off-white">
        <Content className={styles.uspWrapper}>
          <TrackerUSP />
        </Content>

        <Content className={styles.contentSectionWrapper}>
          <div className={styles.contentFlexbox}>
            {
              contentSectionData.slice(0, 3).map(({
                contentArea,
                imageAlt,
                imageArea,
                title,
              }, index) => (
                <ContentSection
                  key={`tracker-content-section-${index}`}
                  contentBody={contentArea}
                  imgSrc={generateImage({ imageAlt, imageArea })}
                  reverse={index === 1}
                  title={title}
                />
              ))
            }
          </div>
        </Content>
      </RoundedSection>
      <RoundedSection withBottomLedge withTopLedge>
        <Content className={styles.expandableSectionWrapper}>
          {
            expandableSectionData.map(({
              content,
              description,
              title,
            }, index) => (
              <ExpandableSection
                key={`tracker-expandable-${index}`}
                content={content}
                description={description}
                reverse={index === 0}
                title={title}
              />
            ))
          }
        </Content>
      </RoundedSection>
      <RoundedSection withBottomLedge withTopLedge theme="off-white">
        <Content className={styles.contentSectionWrapper}>
          <div className={styles.contentGrid}>
            {
              contentSectionData.slice(3, 7).map(({
                contentArea,
                imageAlt,
                imageArea,
                title,
              }, index) => (
                <ContentSection
                  {...getContentSectionTheme(index)}
                  key={`tracker-content-section-${index}`}
                  contentBody={contentArea}
                  imgSrc={generateImage({ imageAlt, imageArea })}
                  reverse={index === 0}
                  title={title}
                />
              ))
            }
          </div>
        </Content>
      </RoundedSection>
      <RoundedSection withBottomLedge withTopLedge>
        <Content className={styles.similarArticlesWrapper}>
          <SimilarArticles acf={similarArticlesData} />
        </Content>

        {/* @ts-expect-error */}
        <TrackerFAQ title={acf?.faq_title} {...{ faq }} />
      </RoundedSection>
    </div>
  );
};

export default TrackerHome;
